/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTypeProvider } from '@devexpress/dx-react-grid';
import { Table } from '@devexpress/dx-react-grid-material-ui';
import ReactSelect from 'react-select';

import TableCell from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import { TextField, makeStyles } from '@material-ui/core';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { toast } from 'react-hot-toast';
import { formatTaskDate, toTaskDateInputValue } from '../../../../utils/taskDate';

const PREFIX = 'Demo';
const classes = {
  lookupEditCell: `${PREFIX}-lookupEditCell`,
  dialog: `${PREFIX}-dialog`,
  inputRoot: `${PREFIX}-inputRoot`,
  selectMenu: `${PREFIX}-selectMenu`
};
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${classes.lookupEditCell}`]: {
    padding: theme.spacing(1)
  },
  [`& .${classes.dialog}`]: {
    width: 'calc(100% - 16px)'
  },
  [`& .${classes.inputRoot}`]: {
    width: '100%'
  },
  [`& .${classes.selectMenu}`]: {
    position: 'absolute !important'
  }
}));

const useStyles = makeStyles({
  root: {
    '& tfoot': {
      display: 'none'
    }
  }
});

// Starts inline editing on focus and displays validation errors for TotalTask fields.
export const TotalTaskFocusableCell = ({
  onClick,
  validationStatus = {},
  setValidationStatus = () => {},
  ...restProps
}) => {
  const { row, column } = restProps;
  const fieldStatus = validationStatus[row.id]?.[column.name];
  const isInvalid = fieldStatus?.isValid === false;
  const actualHoursLocked = column.name === 'actualHours' && row.status !== 'Complete';

  const handleFocus = (event) => {
    // Focus bubbles from the lookup <select>. Starting editing again at that point
    // interrupts the select's change/blur commit lifecycle.
    if (event.currentTarget !== event.target) return;
    if (actualHoursLocked) return;

    if (isInvalid) {
      setValidationStatus((previous) => ({
        ...previous,
        [row.id]: {
          ...previous[row.id],
          [column.name]: undefined
        }
      }));
    }
    onClick?.(); // only call it if it exists
  };

  return (
    <Table.Cell
      {...restProps}
      tabIndex={0}
      onFocus={handleFocus}
      style={{
        ...restProps.style,
        border: isInvalid ? '1px solid red' : restProps.style?.border,
        backgroundColor: actualHoursLocked ? '#fafafa' : restProps.style?.backgroundColor
      }}
      title={
        isInvalid
          ? fieldStatus.error
          : actualHoursLocked
          ? 'Actual hours can only be edited when the task is Complete'
          : undefined
      }
    />
  );
};

const staticLookupOptions = {
  type: [
    { label: 'Site Item', value: 'Site Item' },
    { label: 'Defect', value: 'Defect' },
    { label: 'RFI', value: 'RFI' },
    { label: 'Design Change', value: 'Design Change' },
    { label: 'Variation', value: 'Variation' },
    { label: 'Estimate', value: 'Estimate' },
    { label: 'Service Project', value: 'Service Project' },
    { label: 'Internal Office', value: 'Internal Office' }
  ],
  status: [
    { label: 'Draft', value: 'Draft' },
    { label: 'Assigned', value: 'Assigned' },
    { label: 'Pending', value: 'Pending' },
    { label: 'Submitted for Approval', value: 'Submitted for Approval' },
    { label: 'Complete', value: 'Complete' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Cancelled', value: 'Cancelled' }
  ],
  priority: ['Critical', 'High', 'Normal', 'Low'].map((value) => ({ label: value, value })),
  dependencyStatus: [
    { label: 'Assigned', value: 'Assigned' },
    { label: 'Pending', value: 'Pending' },
    { label: 'Submitted for Approval', value: 'Submitted for Approval' },
    { label: 'Complete', value: 'Complete' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Cancelled', value: 'Cancelled' }
  ]
};

const TotalTaskLookupEditor = ({ column, row, value, onValueChange, onBlur, autoFocus, optionsMap = {} }) => {
  const classes = useStyles();
  const options = optionsMap[column.name] || staticLookupOptions[column.name] || [];

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const columnName = column.name;

  const resolveSelectedOption = () => {
    if (value === null) return null;

    const valueId = typeof value === 'object' ? value?.id || value?.value : value;
    const valueOption = options.find((option) => String(option.value) === String(valueId));
    if (valueOption) return valueOption;

    const modelValue =
      columnName === 'assignedTo'
        ? row?._raw?.assignedTo?.[0]?.employeeId?._id || row?._raw?.assignedTo?.[0]?.employeeId
        : columnName === 'class'
        ? row?._raw?.class?.id
        : value;
    return options.find((option) => String(option.value) === String(modelValue)) || null;
  };

  const [selectedOption, setSelectedOption] = useState(resolveSelectedOption);
  const hasInteracted = useRef(false);

  useEffect(() => {
    if (!hasInteracted.current) setSelectedOption(resolveSelectedOption());
    // options can arrive asynchronously after the edit cell opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value]);

  const handleChange = (option) => {
    if (
      columnName === 'status' &&
      option?.value === 'Pending' &&
      (!row?._raw?.workflow?.pendingReason || !row?._raw?.workflow?.pendingComment)
    ) {
      toast.error('Pending status requires a pending reason and comment. Double-click the task to add them.');
      return;
    }

    hasInteracted.current = true;
    setSelectedOption(option);
    onValueChange(option?.value ?? null);
  };

  return (
    <>
      <StyledTableCell className={classes.lookupEditCell}>
        <ReactSelect
          onBlur={isSmall ? null : onBlur}
          autoFocus={autoFocus}
          className="basic-single"
          classNamePrefix="select"
          value={selectedOption}
          isSearchable={true}
          name="color"
          options={options}
          isClearable={columnName === 'assignedTo' || columnName === 'class'}
          // eslint-disable-next-line no-undef
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          onChange={handleChange}
        />
      </StyledTableCell>
    </>
  );
};

export const TotalTaskLookupProvider = ({ optionsMap, ...props }) => (
  <DataTypeProvider
    editorComponent={(editorProps) => <TotalTaskLookupEditor {...editorProps} optionsMap={optionsMap} />}
    {...props}
  />
);

const TotalTaskDependenciesEditor = ({ row, value, onValueChange, onBlur, autoFocus, options = [] }) => {
  const availableOptions = useMemo(
    () => options.filter((option) => String(option.value) !== String(row.id || row._id)),
    [options, row.id, row._id]
  );
  const initialIds = Array.isArray(value)
    ? value
    : row?.dependencyIds ||
      row?._raw?.dependencies?.map((dependency) => dependency?._id || dependency).filter(Boolean) ||
      [];
  const resolveSelectedOptions = () =>
    availableOptions.filter((option) => initialIds.some((selectedId) => String(selectedId) === String(option.value)));
  const [selectedOptions, setSelectedOptions] = useState(resolveSelectedOptions);
  const hasInteracted = useRef(false);

  useEffect(() => {
    if (!hasInteracted.current) setSelectedOptions(resolveSelectedOptions());
    // options can arrive asynchronously after the edit cell opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableOptions, value]);

  const handleChange = (selected) => {
    const nextSelection = selected || [];
    hasInteracted.current = true;
    setSelectedOptions(nextSelection);
    onValueChange(nextSelection.map((option) => option.value));
  };

  return (
    <StyledTableCell className={classes.lookupEditCell}>
      <ReactSelect
        isMulti
        autoFocus={autoFocus}
        value={selectedOptions}
        options={availableOptions}
        onBlur={onBlur}
        isSearchable
        isClearable
        // eslint-disable-next-line no-undef
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        onChange={handleChange}
        placeholder="Select dependencies"
      />
    </StyledTableCell>
  );
};

export const TotalTaskDependenciesProvider = ({ options, ...props }) => (
  <DataTypeProvider
    editorComponent={(editorProps) => <TotalTaskDependenciesEditor {...editorProps} options={options} />}
    {...props}
  />
);

const TotalTaskSupportingStaffEditor = ({ row, value, onValueChange, onBlur, autoFocus, options = [] }) => {
  const initialIds = Array.isArray(value)
    ? value
    : (row?._raw?.supportingStaff || []).map((staff) => staff?.employeeId?._id || staff?.employeeId).filter(Boolean);
  const selectedOptions = options.filter((option) =>
    initialIds.some((employeeId) => String(employeeId) === String(option.value))
  );

  return (
    <StyledTableCell className={classes.lookupEditCell}>
      <ReactSelect
        isMulti
        isClearable
        isSearchable
        autoFocus={autoFocus}
        value={selectedOptions}
        options={options}
        onBlur={onBlur}
        onChange={(selected) => onValueChange((selected || []).map((option) => option.value))}
        placeholder="Select supporting staff"
        // eslint-disable-next-line no-undef
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    </StyledTableCell>
  );
};

export const TotalTaskSupportingStaffProvider = ({ options, ...props }) => (
  <DataTypeProvider
    editorComponent={(editorProps) => <TotalTaskSupportingStaffEditor {...editorProps} options={options} />}
    {...props}
  />
);

const TotalTaskTextEditor = ({ column, value, onValueChange, onBlur, autoFocus }) => {
  const isHoursField = ['estimatedHours', 'actualHours'].includes(column.name);

  return (
    <StyledTableCell className={classes.lookupEditCell}>
      <TextField
        autoFocus={autoFocus}
        onBlur={onBlur}
        type={isHoursField ? 'number' : 'text'}
        inputProps={isHoursField ? { min: 0, step: 0.25 } : undefined}
        value={value ?? ''}
        onChange={(event) => onValueChange(event.target.value)}
      />
    </StyledTableCell>
  );
};

export const TotalTaskTextProvider = (props) => <DataTypeProvider editorComponent={TotalTaskTextEditor} {...props} />;
const Calendar = ({ ...props }) => {
  const { value, onValueChange, column, autoFocus, onBlur } = props;

  return (
    <td>
      <TextField
        autoFocus={autoFocus}
        onBlur={onBlur}
        type="date"
        onChange={(event) => onValueChange(event.target.value)}
        name={column.name}
        value={column.name === 'dueDate' ? toTaskDateInputValue(value) : value ?? ''}
      />
    </td>
  );
};

const DateFormatter = ({ value }) => {
  if (!value) return <span>—</span>;

  return <span>{formatTaskDate(value)}</span>;
};

export const CalendarProvider = (props) => {
  return <DataTypeProvider formatterComponent={DateFormatter} editorComponent={Calendar} {...props} />;
};
